// Firebase Auth wrapper.
//
// Single-user app. We use Google sign-in (popup) because it's the lightest
// config — no email/password, no SMS. The backend enforces
// `allowed_firebase_uid`, so even if somebody else signs in on the client
// they can't read anything. The client-side UID check is a UX nicety only.
//
// Local-first mode (`VITE_AUTH_DISABLED=true`): no Firebase at all. The
// provider hands out a fixed pseudo-user and a placeholder token, so every
// page renders and every API call carries a bearer header the local
// `roast serve` backend ignores.

import type { User } from "firebase/auth";
import type { FirebaseApp } from "firebase/app";
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { env } from "./env";

// Firebase is imported lazily so a local-first build never initialises it
// (and never needs its config). Both modules are ESM; `import()` keeps
// them in the bundle but off the startup path.
async function firebaseApp(): Promise<FirebaseApp> {
  const { initializeApp, getApps, getApp } = await import("firebase/app");
  if (getApps().length > 0) return getApp();
  return initializeApp({
    apiKey: env.firebase.apiKey,
    authDomain: env.firebase.authDomain,
    projectId: env.firebase.projectId,
    appId: env.firebase.appId,
  });
}

// What the UI needs from a signed-in user. Firebase's `User` satisfies it;
// so does the local-mode stand-in.
export interface AuthUser {
  uid: string;
  email: string | null;
  displayName?: string | null;
}

export interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  // Whether the current signed-in user matches the backend's allowed UID.
  // `null` means "we don't know yet" (still loading) or the gate isn't
  // configured on the client.
  authorized: boolean | null;
  // True in VITE_AUTH_DISABLED mode: no Firebase, no login page.
  authDisabled: boolean;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
  // Always-fresh Firebase ID token to pass to the REST facade.
  // The call throws if the user isn't signed in — callers should check.
  getIdToken: () => Promise<string>;
}

const AuthCtx = createContext<AuthContextValue | null>(null);

const LOCAL_USER: AuthUser = {
  uid: "local",
  email: null,
  displayName: "local operator",
};
// The local server does not read it; the header just has to be present
// so the client code path is identical in both modes.
const LOCAL_TOKEN = "local-dev";

export function AuthProvider({ children }: { children: ReactNode }) {
  if (env.authDisabled) {
    return <LocalAuthProvider>{children}</LocalAuthProvider>;
  }
  return <FirebaseAuthProvider>{children}</FirebaseAuthProvider>;
}

function LocalAuthProvider({ children }: { children: ReactNode }) {
  const value = useMemo<AuthContextValue>(
    () => ({
      user: LOCAL_USER,
      loading: false,
      authorized: true,
      authDisabled: true,
      signIn: async () => {},
      signOut: async () => {},
      getIdToken: async () => LOCAL_TOKEN,
    }),
    [],
  );
  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

function FirebaseAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let unsub: (() => void) | null = null;
    let cancelled = false;
    (async () => {
      const { getAuth, onAuthStateChanged } = await import("firebase/auth");
      const auth = getAuth(await firebaseApp());
      if (cancelled) return;
      // Pass an explicit error callback — Firebase emits permissions and
      // API-configuration errors through this path, and without it a bad
      // config leaves `loading` stuck at `true` with no visible failure.
      unsub = onAuthStateChanged(
        auth,
        (u) => {
          setUser(u);
          setLoading(false);
        },
        (err) => {
          console.error("[auth] onAuthStateChanged error:", err);
          setLoading(false);
        },
      );
    })().catch((err) => {
      console.error("[auth] firebase init failed:", err);
      setLoading(false);
    });
    return () => {
      cancelled = true;
      unsub?.();
    };
  }, []);

  const value: AuthContextValue = useMemo(() => {
    return {
      user,
      loading,
      authorized: (() => {
        if (!user) return null;
        if (!env.allowedUid) return true; // dev-mode passthrough
        return user.uid === env.allowedUid;
      })(),
      authDisabled: false,
      signIn: async () => {
        const { getAuth, GoogleAuthProvider, signInWithPopup } = await import("firebase/auth");
        const auth = getAuth(await firebaseApp());
        await signInWithPopup(auth, new GoogleAuthProvider());
      },
      signOut: async () => {
        const { getAuth, signOut } = await import("firebase/auth");
        const auth = getAuth(await firebaseApp());
        await signOut(auth);
      },
      getIdToken: async () => {
        const { getAuth } = await import("firebase/auth");
        const auth = getAuth(await firebaseApp());
        if (!auth.currentUser) throw new Error("not signed in");
        return auth.currentUser.getIdToken();
      },
    };
  }, [user, loading]);

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
